import { useOneButtonStore, type FinancingProduct } from '../../../../store/oneButtonStore';
import { isEnfin, isGoodLeap, partnerKeyForSyncModal } from './productsFinanceHubHelpers';
import type { ProductsFinanceHubContextValue } from './ProductsFinanceHubContext.types';

export type QuoteSyncTarget = {
    productTypeName: 'Solar' | 'Roof' | 'Battery';
    gridPartner?: string;
    quotePartners?: string[];
    productTypes?: Array<'Solar' | 'Roof' | 'Battery'>;
};

function normalizePartner(partner: string): string {
    return String(partner || '').replace(/\s+/g, '').toLowerCase();
}

function activeOpportunityProducts(opportunityDetails: any | null | undefined): any[] {
    const raw = opportunityDetails?.OpportunityProduct ?? opportunityDetails?.opportunityProduct ?? [];
    const arr = Array.isArray(raw) ? raw : [];
    return arr.filter((p: any) => p.ActiveFlag !== false && p.activeFlag !== false);
}

/** Map a financing grid / AI plan column to the primary opportunity product type for Partner Sync. */
export function productTypeFromFinancingProduct(col: FinancingProduct | string): 'Solar' | 'Roof' | 'Battery' {
    if (col === 'Roof' || col === 'Battery + Roof') return 'Roof';
    if (col === 'Battery') return 'Battery';
    return 'Solar';
}

export function resolveQuoteSyncTargetProduct(
    opportunityDetails: any | null | undefined,
    productTypeName: 'Solar' | 'Roof' | 'Battery',
    gridPartner?: string,
): { productName: string; partnerDisplayName: string } | null {
    const active = activeOpportunityProducts(opportunityDetails);
    if (!active.length) return null;

    const byType = active.filter((p: any) => p.Product_Type_Name === productTypeName);
    if (gridPartner) {
        const partnerMatch = byType.find(
            (p: any) =>
                normalizePartner(String(p.Finance_Partner_Display_Name ?? '')) === normalizePartner(gridPartner),
        );
        if (partnerMatch) {
            return {
                productName: String(partnerMatch.Product_Type_Name ?? productTypeName),
                partnerDisplayName: String(partnerMatch.Finance_Partner_Display_Name ?? '').trim(),
            };
        }

        // Grid partner is authoritative for which sync flow to open even when opp product uses another label.
        return {
            productName: productTypeName,
            partnerDisplayName: gridPartner,
        };
    }

    const first = byType[0] ?? active.find((p: any) => p.Product_Type_Name === 'Solar') ?? active[0];
    if (!first) return null;

    return {
        productName: String(first.Product_Type_Name ?? productTypeName),
        partnerDisplayName: String(first.Finance_Partner_Display_Name ?? '').trim(),
    };
}

/** Best-effort target when the user did not click a specific grid cell / plan. */
export function defaultQuoteSyncTarget(opportunityDetails: any | null | undefined): QuoteSyncTarget | undefined {
    const active = activeOpportunityProducts(opportunityDetails);
    if (!active.length) return undefined;

    const solar =
        active.find((p: any) => p.Product_Type_Name === 'Solar') ??
        active.find((p: any) => p.Product_Type_Name === 'Roof') ??
        active[0];

    const productTypeName = (['Solar', 'Roof', 'Battery'] as const).includes(solar?.Product_Type_Name)
        ? (solar.Product_Type_Name as 'Solar' | 'Roof' | 'Battery')
        : 'Solar';

    return {
        productTypeName,
        gridPartner: String(solar?.Finance_Partner_Display_Name ?? '').trim() || undefined,
    };
}

type PartnerSyncHub = Pick<ProductsFinanceHubContextValue, 'setPartnerModal' | 'setLeadSyncFormOpen'>;

/** Opens the same Partner Sync flow as the system design card header (GoodLeap/Enfin → lead form; others → checklist modal). */
export function openPartnerSyncForQuoteTarget(
    target: QuoteSyncTarget,
    opportunityDetails: any | null | undefined,
    hubCtx: PartnerSyncHub | null,
): boolean {
    const resolved = resolveQuoteSyncTargetProduct(
        opportunityDetails,
        target.productTypeName,
        target.gridPartner,
    );
    const partnerDisplayName = resolved?.partnerDisplayName ?? target.gridPartner ?? '';
    const productName = resolved?.productName ?? target.productTypeName;
    if (!partnerDisplayName) return false;

    const syncKey = partnerKeyForSyncModal(partnerDisplayName);
    if (isGoodLeap(partnerDisplayName) || isEnfin(partnerDisplayName)) {
        const openLeadSync = hubCtx?.setLeadSyncFormOpen ?? useOneButtonStore.getState().setLeadSyncFormOpen;
        openLeadSync(true, 'sync_existing', syncKey, { hideCreateLead: true });
        return true;
    }

    if (hubCtx?.setPartnerModal) {
        hubCtx.setPartnerModal({ partnerName: syncKey, productName });
        return true;
    }

    return false;
}
